/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import Apps from '@react/react-spectrum/Icon/Apps';
import Button from '@react/react-spectrum/Button';
import classNames from 'classnames';
import I18nProvider from '@adobe/adobe-intl-react';
import Popover from '@react/react-spectrum/Popover';
import PropTypes from 'prop-types';
import React, {lazy, Suspense, useState} from 'react';
import ShellOverlayTrigger from './ShellOverlayTrigger';
import ShellWait from './ShellWait';
import * as unifiedNavigationLocaleData from './__localization__/UnifiedNavigation';
import './UnifiedNavigation.scss';

const UnifiedNavigation = lazy(() => import('./UnifiedNavigation'));

export default function UnifiedNavigationPopover(props) {
  let {darkestTheme, enableThemes, formatMessage, locale, solutions} = props;
  let [open, setOpen] = useState(false);
  /**
   * Handler for opening and closing Unified Navigation.
   */
  const toggleUnifiedNav = () => {
    setOpen(!open);
  };

  return (
    <React.Fragment>
      <ShellOverlayTrigger
        element="unified nav"
        onHide={e => toggleUnifiedNav(e)}
        onShow={e => toggleUnifiedNav(e)}
        placement="bottom"
        trigger="click">
        <Button
          aria-label={formatMessage({
            defaultMessage: 'Navigate in the Experience Cloud',
            description: 'Label for Unified Navigation',
            messageId: 'UnifiedNavigation'})}
          className="unified-nav-button"
          data-testid="unified-nav-button"
          key="unified-nav-button"
          onClick={e => toggleUnifiedNav(e)}
          variant="tool">
          <Apps size="S" />
        </Button>
        <Popover
          {...props}
          className={classNames('unified-nav-popover', {
            'spectrum--darkest': enableThemes && darkestTheme,
            'spectrum--light': enableThemes && !darkestTheme
          })}
          isDialog={false}
          role="dialog">
          <Suspense fallback={<ShellWait data-testid="unified-nav-wait" locale={locale} size="M" />}>
            <I18nProvider locale={locale} localeData={unifiedNavigationLocaleData}>
              <UnifiedNavigation
                darkestTheme={darkestTheme}
                enableThemes={enableThemes}
                locale={locale}
                open={open}
                solutions={solutions}
                toggleUnifiedNav={toggleUnifiedNav}
                {...props}
              />
            </I18nProvider>
          </Suspense>
        </Popover>
      </ShellOverlayTrigger>
      <div
        className={classNames('spectrum-Underlay', 'unified-underlay', {'is-open': open})}
      />
    </React.Fragment>
  );
}

UnifiedNavigation.propTypes = {
  locale: PropTypes.string
};
